import React, { useEffect, useMemo, useState } from "react";
import LoaderSpiner from "../../Components/LoaderSpiner";
import styles from "./VendorRechargeHistory.module.css";

const VendorRechargeHistory = ({ refreshKey, vendorId }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(
          "https://prod.toxboxai.com/api/owner/vendors?page=1&limit=100",  // Increased limit for safety
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
          throw new Error("Server returned non-JSON response");
        }

        const data = await response.json();
        if (data.success) {
          setVendors(data.data);
        } else {
          setError("Failed to fetch vendor data");
        }
      } catch (err) {
        setError("Error fetching data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, [refreshKey]);  // Refetch only on refreshKey (vendorId changes don't trigger refetch)

  // Memoize the selected vendor's history
  const selectedVendorHistory = useMemo(() => {
    if (!vendorId || vendors.length === 0) {
      return null;
    }

    const selectedVendor = vendors.find((vendor) => vendor._id === vendorId);
    if (!selectedVendor || !selectedVendor.rechargeHistory?.length) {
      return (
        <div className={styles.vendorCard}>
          <h2 className={styles.vendorName}>{selectedVendor?.name || "Unknown Vendor"}</h2>
          <p className={styles.noHistory}>No recharge history available</p>
        </div>
      );
    }

    return (
      <div className={styles.vendorCard}>
        <h2 className={styles.vendorName}>{selectedVendor.name}</h2>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr>
              <th className={styles.tableHeadCell}>Amount</th>
              <th className={styles.tableHeadCell}>Date</th>
              <th className={styles.tableHeadCell}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {selectedVendor.rechargeHistory.map((recharge) => (
              <tr key={recharge._id} className={styles.tableRow}>
                <td className={styles.tableCell}>{recharge.amount}</td>
                <td className={styles.tableCell}>
                  {new Date(recharge.date).toLocaleString()}
                </td>
                <td className={styles.tableCell}>
                  {recharge.notes || "Not Avable"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [vendors, vendorId]);

  if (loading) return <LoaderSpiner />;
  if (error) return <div className={styles.error}>{error}</div>;

  if (!vendorId) return null;  // Hide if no vendor selected

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Vendor Recharge History</h1>
      {selectedVendorHistory}
    </div>
  );
};

export default React.memo(VendorRechargeHistory);